import React, { useEffect, useState } from "react";
import { ArrowTopRightOnSquareIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import StatusBadge from "../../../components/common/StatusBadge";
import PremiumModelPicker from "../../../components/common/PremiumModelPicker";
import { useToast } from "../../../contexts/ToastContext";
import { setCurrentProject } from "../../../redux/project/projectSlice";
import { projectsService } from "../../../services/projects";
import { handleBillingError } from "../../../utils/billing";

const General = ({ currentUser }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const project = useSelector((state) => state.project.currentProject);
  const [name, setName] = useState(project?.name || "");
  const [domain, setDomain] = useState(project?.custom_domain || "");
  const [domainResult, setDomainResult] = useState(null);
  const [domainBusy, setDomainBusy] = useState(false);
  const [domainAction, setDomainAction] = useState("");
  const [modelOverride, setModelOverride] = useState(project?.model_override || "");
  const isStaff = Boolean(currentUser?.identity?.is_staff);
  const connectedDomain = (project?.custom_domain || "").trim().toLowerCase();
  const domainStatus = domainResult?.status;
  const domainPending = Boolean(connectedDomain) && domainStatus !== "active";
  const domainBadgeStatus = domainStatus === "pending" ? "pending_verification" : domainStatus;
  const productionUrl = connectedDomain
    ? `https://${connectedDomain}`
    : (project?.production_url || (project?.platform_cname ? `https://${project.platform_cname}` : ""));
  const productionDisplay = productionUrl.replace(/^https?:\/\//, "") || "Not available yet";

  useEffect(() => {
    setName(project?.name || "");
    setDomain(project?.custom_domain || "");
    setModelOverride(project?.model_override || "");
  }, [project?.hash, project?.name, project?.custom_domain, project?.model_override]);
  useEffect(() => {
    if (!project?.hash || !project.cf_pages_project) return undefined;
    const timer = setTimeout(() => {
      projectsService.verifyCustomDomain(
        project.hash,
        domain.trim(),
        (response) => setDomainResult(response.data),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [project?.hash, project?.cf_pages_project, domain]);

  const save = (data) => {
    projectsService.updateProject(project.hash, data, (response) => {
      dispatch(setCurrentProject(response.data));
    });
  };

  const dnsRecords = domainResult?.dns_records || [];

  const copyProductionUrl = async () => {
    if (!productionUrl) return;
    await navigator.clipboard.writeText(productionUrl);
    success("Copied", 2000);
  };

  const connectDomain = () => {
    const next = domain.trim().toLowerCase();
    if (!next || domainBusy) return;
    setDomainBusy(true);
    setDomainAction("connect");
    projectsService.setCustomDomain(
      project.hash,
      next,
      (response) => {
        setDomainResult(response.data);
        dispatch(setCurrentProject({
          ...project,
          custom_domain: next,
          production_url: `https://${next}`,
          discoverable: true,
        }));
        setDomainBusy(false);
        setDomainAction("");
      },
      (err) => {
        setDomainBusy(false);
        setDomainAction("");
        if (handleBillingError(err, toastError)) {
          navigate("/account/billing/");
          return;
        }
      },
    );
  };

  const verifyDomain = () => {
    if (domainBusy) return;
    setDomainBusy(true);
    setDomainAction("verify");
    projectsService.verifyCustomDomain(
      project.hash,
      connectedDomain || domain.trim(),
      (response) => {
        setDomainResult(response.data);
        setDomainBusy(false);
        setDomainAction("");
      },
      () => {
        setDomainBusy(false);
        setDomainAction("");
      },
    );
  };

  const disconnectDomain = () => {
    if (domainBusy) return;
    if (!window.confirm("Disconnect this custom domain from the site?")) return;
    setDomainBusy(true);
    setDomainAction("disconnect");
    projectsService.clearCustomDomain(
      project.hash,
      (response) => {
        setDomainResult(response.data);
        setDomain("");
        dispatch(setCurrentProject({
          ...project,
          custom_domain: "",
          production_url: project.platform_cname
            ? `https://${project.platform_cname}`
            : "",
        }));
        setDomainBusy(false);
        setDomainAction("");
      },
      () => {
        setDomainBusy(false);
        setDomainAction("");
      },
    );
  };

  return (
    <div className="max-w-2xl space-y-8">
      <section>
        <h2 className="mb-4 text-sm font-semibold">Site details</h2>
        <div className="space-y-4">
          <label className="block text-xs font-medium">
            Site name
            <div className="mt-2 flex items-center gap-2">
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="min-w-0 flex-1 rounded-lg border border-theme-secondary px-4 py-3 text-sm"
              />
              <button
                type="button"
                disabled={name === (project?.name || "")}
                onClick={() => save({ name })}
                className="shrink-0 rounded-lg bg-theme-primary px-5 py-3 text-sm font-semibold text-theme-button disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </label>
        </div>
      </section>

      {isStaff && (
        <section>
          <h2 className="mb-4 text-sm font-semibold">Model</h2>
          <PremiumModelPicker
            value={modelOverride}
            onChange={(next) => {
              setModelOverride(next);
              save({ model_override: next });
            }}
          />
        </section>
      )}

      <section>
        <h2 className="mb-4 text-sm font-semibold">Domain</h2>
        <div className="space-y-5">
          <div>
            <p className="text-xs font-medium">Production URL</p>
            <div className="mt-2 flex min-w-0 items-center gap-1.5">
              {productionUrl ? (
                <span className="min-w-0 truncate text-sm text-theme-primary">
                  {productionDisplay}
                </span>
              ) : (
                <span className="text-sm opacity-60">{productionDisplay}</span>
              )}
              {productionUrl && (
                <>
                  <button
                    type="button"
                    onClick={copyProductionUrl}
                    title="Copy URL"
                    aria-label="Copy production URL"
                    className="shrink-0 rounded p-0.5 text-theme-text opacity-50 transition hover:bg-theme-secondary hover:opacity-100"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href={productionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    aria-label="Open production URL in new tab"
                    className="shrink-0 rounded p-0.5 text-theme-text opacity-50 transition hover:bg-theme-secondary hover:opacity-100"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </a>
                </>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium">
              Custom Domain
              {project?.is_blueprint && (
                <span className="font-normal opacity-50"> · requires Launch plan or higher</span>
              )}
            </p>
            <div className="mt-2 rounded-xl border border-theme-secondary p-5">
              <div className="flex gap-2">
                <input
                  value={domain}
                  onChange={(event) => setDomain(event.target.value)}
                  disabled={Boolean(connectedDomain) || domainBusy}
                  className="min-w-0 flex-1 rounded-lg border border-theme-secondary px-4 py-2 text-sm disabled:opacity-60"
                  placeholder="example.com"
                />
                {!connectedDomain ? (
                  <button
                    type="button"
                    onClick={connectDomain}
                    disabled={domainBusy || !domain.trim()}
                    className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
                  >
                    {domainAction === "connect" ? "Connecting…" : "Connect"}
                  </button>
                ) : (
                  <>
                    {domainPending && (
                      <button
                        type="button"
                        onClick={verifyDomain}
                        disabled={domainBusy}
                        className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
                      >
                        {domainAction === "verify" ? "Checking…" : "Verify"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={disconnectDomain}
                      disabled={domainBusy}
                      className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
                    >
                      {domainAction === "disconnect" ? "Disconnecting…" : "Disconnect"}
                    </button>
                  </>
                )}
              </div>
              {domainBadgeStatus && domainBadgeStatus !== "not_configured" && (
                <div className="mt-3">
                  <StatusBadge status={domainBadgeStatus} />
                </div>
              )}
              <p className="mt-4 text-xs opacity-60">
                {dnsRecords.length > 1
                  ? "Add these DNS records at your provider:"
                  : dnsRecords.length === 1
                    ? "Add this DNS record at your provider:"
                    : "Enter a domain above to see DNS records."}
              </p>
              {dnsRecords.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {dnsRecords.map((record) => (
                    <code
                      key={`${record.type}-${record.name}`}
                      className="block rounded-lg bg-[#26211a] p-3 text-[11.5px] text-[#e8e2d6]"
                    >
                      {record.type} {record.name} → {record.value}
                    </code>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default General;
